import { useMemo, useState, type FormEvent } from 'react';
import { addDoc, collection, deleteDoc, doc } from 'firebase/firestore';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { TextField } from '../components/TextField';
import { db } from '../lib/firebase';
import { useAdminEventoData } from './AdminEventoDataProvider';

export function MesasScreen() {
  const { eventoId, mesas, invitaciones } = useAdminEventoData();

  const ocupacion = useMemo(() => {
    const m = new Map<string, number>();
    for (const i of invitaciones) {
      if (!i.mesaId || i.estado === 'anulada') continue;
      const personas = 1 + (i.invitado.acompanante ? 1 : 0);
      m.set(i.mesaId, (m.get(i.mesaId) ?? 0) + personas);
    }
    return m;
  }, [invitaciones]);

  const [numero, setNumero] = useState('');
  const [nombre, setNombre] = useState('');
  const [capacidad, setCapacidad] = useState('10');
  const [guardando, setGuardando] = useState(false);

  async function agregar(e: FormEvent) {
    e.preventDefault();
    setGuardando(true);
    try {
      await addDoc(collection(db, 'eventos', eventoId, 'mesas'), {
        numero: Number(numero) || 0,
        nombre: nombre.trim() || `Mesa ${numero}`,
        capacidad: Number(capacidad) || 0,
        x: null,
        y: null,
        notas: '',
      });
      setNumero('');
      setNombre('');
      setCapacidad('10');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-5">
      <ul className="space-y-2">
        {mesas.map((m) => {
          const usadas = ocupacion.get(m.id) ?? 0;
          const lleno = usadas >= m.capacidad;
          return (
            <li key={m.id}>
              <Card className="flex items-center justify-between gap-3 p-4">
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <strong className="truncate text-sm text-texto">
                    {m.numero} · {m.nombre}
                  </strong>
                  <span className="flex items-center gap-2">
                    <span className={['text-xs', lleno ? 'text-red-600' : 'text-texto/50'].join(' ')}>
                      {usadas} / {m.capacidad} personas
                    </span>
                    <BarraOcupacion usadas={usadas} capacidad={m.capacidad} />
                  </span>
                </span>
                <button
                  type="button"
                  className="shrink-0 rounded-full border border-borde px-3 py-1.5 text-xs font-medium text-texto/70 transition hover:border-red-300 hover:text-red-600 active:scale-[0.98]"
                  onClick={() => void deleteDoc(doc(db, 'eventos', eventoId, 'mesas', m.id))}
                >
                  Borrar
                </button>
              </Card>
            </li>
          );
        })}
        {mesas.length === 0 && (
          <li className="text-sm text-texto/50">Todavía no hay mesas.</li>
        )}
      </ul>

      <Card className="p-5">
        <form className="flex flex-col gap-4" onSubmit={agregar}>
          <h2 className="text-sm font-semibold text-texto">Agregar mesa</h2>
          <div className="grid grid-cols-2 gap-4">
            <TextField
              label="Número"
              type="number"
              inputMode="numeric"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              required
            />
            <TextField
              label="Capacidad"
              type="number"
              inputMode="numeric"
              value={capacidad}
              onChange={(e) => setCapacidad(e.target.value)}
            />
          </div>
          <TextField label="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} hint="Opcional" />
          <Button type="submit" variante="secundario" cargando={guardando} className="self-end">
            Agregar mesa
          </Button>
        </form>
      </Card>
    </div>
  );
}

function BarraOcupacion({ usadas, capacidad }: { usadas: number; capacidad: number }) {
  if (!capacidad) return null;
  const proporcion = Math.max(0, Math.min(1, usadas / capacidad));
  const tono = usadas >= capacidad ? 'bg-red-500' : proporcion >= 0.75 ? 'bg-oro' : 'bg-emerald-500';
  return (
    <span className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-navy/10" aria-hidden="true">
      <span className={['block h-full rounded-full transition-all', tono].join(' ')} style={{ width: `${proporcion * 100}%` }} />
    </span>
  );
}
